import { c as _c } from "react/compiler-runtime";
import classNames from 'classnames';
import { type ReactNode, type SyntheticEvent, type TransitionEventHandler, createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Icon } from '../../Icon';
import { getScrollParent } from '../../Popup';
import type { Spacing } from '../../types';
import { LayoutStack } from '../LayoutStack';
import styles from './LayoutAccordion.module.css';
type AccordionState = {
  activeKey?: string;
  prevKey?: string | undefined;
  scrollOnOpen?: boolean;
  onTransitionFinished: (next: string) => void;
};
const AccordionContext = createContext<AccordionState>({
  activeKey: undefined,
  prevKey: undefined,
  onTransitionFinished: _ => {},
  scrollOnOpen: true
});
export function useAccordionContext() {
  return useContext(AccordionContext);
}
export function LayoutAccordionItem({
  label,
  id: key,
  className,
  children,
  onClick
}: {
  id: string;
  label: ReactNode;
  className?: string;
  children?: ReactNode;
  onClick: (key: string) => void;
}) {
  const {
    activeKey,
    onTransitionFinished,
    prevKey,
    scrollOnOpen
  } = useAccordionContext();
  const [open, setOpen] = useState(activeKey === key || prevKey === key);
  const contentRef = useRef<HTMLDivElement>(null);
  const detailsRef = useRef<HTMLDetailsElement>(null);
  const onSummaryClicked = useCallback((event: SyntheticEvent) => {
    event.preventDefault();
    onClick(key);
  }, [onClick, key]);
  const handleTransitionEnd: TransitionEventHandler = useCallback(ev => {
    if (ev.propertyName !== 'opacity') return;
    if (ev.target !== contentRef.current) return;
    if (activeKey !== key) return;
    onTransitionFinished(key);
    if (!scrollOnOpen) return;
    window.requestAnimationFrame(() => {
      if (!detailsRef.current) return;
      const scrollContainer = getScrollParent(detailsRef.current);
      if (!scrollContainer) return;
      const {
        top
      } = detailsRef.current.getBoundingClientRect();
      const offsetTop = scrollContainer === window ? 0 : (scrollContainer as HTMLElement).offsetTop;
      scrollContainer.scrollBy({
        behavior: 'smooth',
        top: top - offsetTop
      });
    });
  }, [activeKey, key, onTransitionFinished, scrollOnOpen]);
  useEffect(() => {
    if (activeKey === prevKey) return;
    const timeout = setTimeout(() => setOpen(activeKey === key), 1);
    return () => clearTimeout(timeout);
  }, [key, activeKey, prevKey]);

  // TODO: Find a way to make this work with axe-core, we need to be able to handle nested links here
  return (
    // eslint-disable-next-line jsx-a11y/role-supports-aria-props
    <details open id={key} aria-expanded={activeKey === key ? 'true' : 'false'} className={classNames(styles.accordionItem, {
      [styles.open!]: open
    }, className)} ref={detailsRef}>
      <summary onClick={onSummaryClicked} className={styles.accordionSummary}>
        {label}
        <Icon name="chevronDown" className={styles.accordionIcon} size="400" />
      </summary>
      <div className={styles.wrapper} onTransitionEnd={handleTransitionEnd} ref={contentRef}>
        <div className={styles.content} inert={activeKey !== key}>
          {children}
        </div>
      </div>
    </details>
  );
}
export type LayoutAccordionProps = {
  activeKey?: string;
  onTransitionFinished?: (next: string, previous: string | undefined) => void;
  defaultKey: string;
  children?: ReactNode;
  className?: string;
  space?: Spacing;
  animationSpeedInMs?: number;
  scrollOnOpen?: boolean;
};
export default function LayoutAccordion(t0) {
  const $ = _c(21);
  const {
    defaultKey,
    children,
    activeKey: externalActiveKey,
    onTransitionFinished,
    className,
    space: t1,
    animationSpeedInMs: t2,
    scrollOnOpen: t3
  } = t0;
  const space = t1 === undefined ? "300" : t1;
  const animationSpeedInMs = t2 === undefined ? 500 : t2;
  const scrollOnOpen = t3 === undefined ? true : t3;
  const [prevKey, setPrevKey] = useState(undefined);
  const [activeKey, setActiveKey] = useState(externalActiveKey ?? defaultKey);
  let t4;
  if ($[0] !== onTransitionFinished || $[1] !== prevKey) {
    t4 = next => {
      onTransitionFinished?.(next, prevKey);
      setPrevKey(next);
    };
    $[0] = onTransitionFinished;
    $[1] = prevKey;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const onFinish = t4;
  if (typeof externalActiveKey === "string" && externalActiveKey !== activeKey) {
    setPrevKey(activeKey);
    setActiveKey(externalActiveKey);
  }
  let t5;
  if ($[3] !== className) {
    t5 = classNames(styles.accordion, className);
    $[3] = className;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const t6 = `${animationSpeedInMs}ms`;
  const t7 = `var(--betterplace-spacing-${space})`;
  let t8;
  if ($[5] !== t6 || $[6] !== t7) {
    t8 = {
      "--animation-speed": t6,
      "--space": t7
    };
    $[5] = t6;
    $[6] = t7;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  const t9 = t8 as React.CSSProperties;
  let t10;
  if ($[8] !== activeKey || $[9] !== onFinish || $[10] !== prevKey || $[11] !== scrollOnOpen) {
    t10 = {
      activeKey,
      onTransitionFinished: onFinish,
      prevKey,
      scrollOnOpen
    };
    $[8] = activeKey;
    $[9] = onFinish;
    $[10] = prevKey;
    $[11] = scrollOnOpen;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  let t11;
  if ($[13] !== children || $[14] !== t10) {
    t11 = <AccordionContext.Provider value={t10}>{children}</AccordionContext.Provider>;
    $[13] = children;
    $[14] = t10;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  let t12;
  if ($[16] !== space || $[17] !== t11 || $[18] !== t5 || $[19] !== t9) {
    t12 = <LayoutStack className={t5} space={space} style={t9}>{t11}</LayoutStack>;
    $[16] = space;
    $[17] = t11;
    $[18] = t5;
    $[19] = t9;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  return t12;
}